import store from 'redux/store';
import { deletePythonLibrary } from 'redux/slices/pythonLibrariesSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditPythonLibrary } from './edit/showEditPythonLibrary';

type PythonLibraryEntity = Extract<ActionEntity, { type: 'python_library' }>;

const isAllPythonLibrary = (entities: readonly ActionEntity[]): entities is readonly PythonLibraryEntity[] =>
  entities.every((entity) => entity.type === 'python_library');

/**
 * Action definitions for Python libraries. `createPythonLibrary` /
 * `addToTransportPythonLibrary` are scoped; `edit` (single-only) and `delete`
 * are contextual.
 */
export const pythonLibraryActionDefinitions: ActionDefinitions = [
  {
    id: 'createPythonLibrary',
    scope: 'type',
    label: 'New Library',
    resolve: (ctx) => () => void showEditPythonLibrary({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllPythonLibrary(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditPythonLibrary({ mode: 'edit', id: only.id }).then(ctx.refresh);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllPythonLibrary(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deletePythonLibrary(entity.id)), {
        refresh,
        confirmBody:
          entities.length === 1
            ? 'Are you sure you want to delete this Python library? Any node definitions linked to it will lose the association.'
            : `Are you sure you want to delete these ${entities.length} Python libraries? Any node definitions linked to them will lose the association.`,
      });
    },
  },
  {
    id: 'addToTransportPythonLibrary',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllPythonLibrary(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'python_library',
        entities.map((entity) => entity.id),
        { objectLabel: single ? single.name : 'Selected Python libraries' }
      );
    },
  },
];
